import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import QuillIcon from './QuillIcon';
import GhostLogo from './GhostLogo';
import OpenAI from 'openai';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY, // Add your API key to .env file
  dangerouslyAllowBrowser: true
});

function App() {
  const [text, setText] = useState('');
  const [cleanedText, setCleanedText] = useState('');
  const [lastProcessedText, setLastProcessedText] = useState('');
  const [textChunks, setTextChunks] = useState([]);
  const [cleanedChunks, setCleanedChunks] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const [showFinishPrompt, setShowFinishPrompt] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
  const [logoVisible, setLogoVisible] = useState(false);
  const [showLogoText, setShowLogoText] = useState(false);
  const [showLogoIcon, setShowLogoIcon] = useState(false);
  const [isCleaningText, setIsCleaningText] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check for saved theme preference or default to system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Check system preference
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showUIElements, setShowUIElements] = useState(false);
  const textareaRef = useRef(null);
  const visibleTextareaRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const finishPromptTimeoutRef = useRef(null);
  const logoTimeoutRef = useRef(null);
  const logoIconTimeoutRef = useRef(null);

  // Dark mode toggle handler
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('theme', newDarkMode ? 'dark' : 'light');
  };

  // Detect if user is on a mobile device
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 1);
  };

  // Get appropriate reveal message based on device type
  const getRevealMessage = () => {
    return isMobileDevice() ? 'tap here to reveal' : '⌘ + ↵ to reveal';
  };

  // Get appropriate keep writing message based on device type
  const getKeepWritingMessage = () => {
    return isMobileDevice() ? 'tap here to keep writing' : '⌘ + ↵ to keep writing';
  };

  // Check if mouse is near any corner
  const isMouseNearCorner = (x, y) => {
    const cornerThreshold = 100; // pixels from corner
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Top-left corner
    if (x <= cornerThreshold && y <= cornerThreshold) return true;
    
    // Top-right corner
    if (x >= windowWidth - cornerThreshold && y <= cornerThreshold) return true;
    
    // Bottom-left corner
    if (x <= cornerThreshold && y >= windowHeight - cornerThreshold) return true;
    
    // Bottom-right corner
    if (x >= windowWidth - cornerThreshold && y >= windowHeight - cornerThreshold) return true;
    
    return false;
  };

  // Handle reveal functionality (for both keyboard shortcut and click/tap)
  const handleReveal = async () => {
    if (!isVisible && text.trim()) {
      // Start cleaning process while still in invisible mode
      setIsCleaningText(true);
      
      let cleaned;
      let updatedCleanedChunks = cleanedChunks;
      
      // Check if text hasn't changed since last processing
      if (text === lastProcessedText && cleanedText) {
        // No changes, just use existing cleaned text
        cleaned = cleanedText;
      } else {
        // Use chunked processing for efficiency
        const { differences, newChunks } = findChunkDifferences(textChunks, text);
        
        if (differences.added.length === 0 && differences.modified.length === 0) {
          // No actual changes to content, use existing cleaned text
          cleaned = cleanedText;
        } else {
          // Process only changed chunks
          updatedCleanedChunks = await processChangedChunks(differences, cleanedChunks);
          cleaned = rebuildTextFromChunks(updatedCleanedChunks);
          
          // Update chunk states
          setTextChunks(newChunks);
          setCleanedChunks(updatedCleanedChunks);
          setLastProcessedText(text);
        }
      }
      
      // Set the cleaned text and start transition
      setCleanedText(cleaned);
      setIsCleaningText(false);
      setIsTransitioning(true);
      
      // Start the blur-to-clear transition
      setTimeout(() => {
        setIsVisible(true);
        setIsTransitioning(false);
      }, 800);
      
      // Start the inactivity timer for visible mode
      logoTimeoutRef.current = setTimeout(() => {
        setLogoVisible(true);
        setShowLogoText(true);
        // Show icon slightly after text
        logoIconTimeoutRef.current = setTimeout(() => {
          setShowLogoIcon(true);
        }, 300);
      }, 6000);
    } else if (!isVisible && !text.trim()) {
      // No text to clean, just show visible mode
      setCleanedText('');
      setLastProcessedText('');
      setIsVisible(true);
      
      // Start the inactivity timer for visible mode
      logoTimeoutRef.current = setTimeout(() => {
        setLogoVisible(true);
        setShowLogoText(true);
        // Show icon slightly after text
        logoIconTimeoutRef.current = setTimeout(() => {
          setShowLogoIcon(true);
        }, 300);
      }, 6000);
    } else {
      // Going from visible to invisible
      // Sync the invisible text with any edits made in visible mode
      setText(cleanedText);
      
      // Update chunks to reflect any manual edits made in visible mode
      if (cleanedText !== rebuildTextFromChunks(cleanedChunks)) {
        // Text was manually edited, update our chunk tracking
        const newChunks = createTextChunks(cleanedText);
        const updatedCleanedChunks = newChunks.map(chunk => ({
          ...chunk,
          cleanedContent: chunk.content,
          processed: true
        }));
        setTextChunks(newChunks);
        setCleanedChunks(updatedCleanedChunks);
        setLastProcessedText(cleanedText);
      }
      
      setIsVisible(false);
      
      // Reset logo visibility for invisible mode
      setLogoVisible(false);
      setShowLogoText(false);
      setShowLogoIcon(false);
      
      // Reset finish prompt when switching to invisible mode
      setShowFinishPrompt(false);
      
      // Clear existing logo timeouts
      if (logoTimeoutRef.current) {
        clearTimeout(logoTimeoutRef.current);
      }
      if (logoIconTimeoutRef.current) {
        clearTimeout(logoIconTimeoutRef.current);
      }
      if (finishPromptTimeoutRef.current) {
        clearTimeout(finishPromptTimeoutRef.current);
      }
      
      // Start the inactivity timer for invisible mode
      logoTimeoutRef.current = setTimeout(() => {
        setLogoVisible(true);
        setShowLogoText(true);
        // Show icon slightly after text
        logoIconTimeoutRef.current = setTimeout(() => {
          setShowLogoIcon(true);
        }, 300);
      }, 6000);
    }
  };

  // Dark Mode Icon Components
  const MoonIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const SunIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2"/>
    </svg>
  );

  // Apply dark mode class to body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  // Logo visibility logic
  useEffect(() => {
    // Show logo container after 0.5 seconds
    const initialLogoTimeout = setTimeout(() => {
      setShowLogo(true);
      setLogoVisible(true);
      // Show text first
      setShowLogoText(true);
      // Show icon slightly after text (additional 0.3 seconds)
      const iconTimeout = setTimeout(() => {
        setShowLogoIcon(true);
      }, 900);
      
      return () => clearTimeout(iconTimeout);
    }, 500);

    return () => clearTimeout(initialLogoTimeout);
  }, []);

  // Cleanup effect for all timeouts
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (finishPromptTimeoutRef.current) {
        clearTimeout(finishPromptTimeoutRef.current);
      }
      if (logoTimeoutRef.current) {
        clearTimeout(logoTimeoutRef.current);
      }
      if (logoIconTimeoutRef.current) {
        clearTimeout(logoIconTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Focus the textarea only when switching to invisible mode
    if (!isVisible && textareaRef.current) {
      textareaRef.current.focus();
      // Position cursor at the end of the text
      const length = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, [isVisible]);

  useEffect(() => {
    // Global keyboard listener for ⌘ + ↵
    const handleGlobalKeyDown = async (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        await handleReveal();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [isVisible, text, lastProcessedText, cleanedText]);

  // Mouse movement tracking for corner proximity
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      
      const nearCorner = isMouseNearCorner(e.clientX, e.clientY);
      setShowUIElements(nearCorner);
    };

    // Only add mouse tracking on non-mobile devices
    if (!isMobileDevice()) {
      document.addEventListener('mousemove', handleMouseMove);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, []);

  const handleKeyDown = (e) => {
    // Only handle typing animation in invisible mode (⌘ + ↵ handled globally now)
    if (!isVisible) {
      // Start typing state
      if (!hasStartedTyping && e.key.length === 1) {
        setHasStartedTyping(true);
      }

      // Handle logo visibility when typing starts
      if (e.key.length === 1) {
        setLogoVisible(false);
        setShowLogoText(false);
        setShowLogoIcon(false);
        
        // Clear existing logo timeouts
        if (logoTimeoutRef.current) {
          clearTimeout(logoTimeoutRef.current);
        }
        if (logoIconTimeoutRef.current) {
          clearTimeout(logoIconTimeoutRef.current);
        }
        
        // Set timeout to show logo again after 6 seconds of no typing
        logoTimeoutRef.current = setTimeout(() => {
          setLogoVisible(true);
          setShowLogoText(true);
          // Show icon slightly after text
          logoIconTimeoutRef.current = setTimeout(() => {
            setShowLogoIcon(true);
          }, 300);
        }, 6000);
      }

      setIsTyping(true);
      
      // Hide finish prompt immediately when typing
      setShowFinishPrompt(false);
      
      // Clear existing timeouts
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (finishPromptTimeoutRef.current) {
        clearTimeout(finishPromptTimeoutRef.current);
      }
      
      // Set new timeout to stop typing animation
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
      }, 150);

      // Set timeout to show finish prompt after 3 seconds of no typing
      if (text.trim() || e.key.length === 1) {
        finishPromptTimeoutRef.current = setTimeout(() => {
          setShowFinishPrompt(true);
        }, 3000);
      }
    }
  };

  const handleChange = (e) => {
    setText(e.target.value);
    
    // Handle logo visibility on text change (invisible mode only)
    if (!isVisible) {
      setLogoVisible(false);
      setShowLogoText(false);
      setShowLogoIcon(false);
      
      // Clear existing logo timeouts
      if (logoTimeoutRef.current) {
        clearTimeout(logoTimeoutRef.current);
      }
      if (logoIconTimeoutRef.current) {
        clearTimeout(logoIconTimeoutRef.current);
      }
      
      // Set timeout to show logo again after 4 seconds of no typing
      logoTimeoutRef.current = setTimeout(() => {
        setLogoVisible(true);
        setShowLogoText(true);
        // Show icon slightly after text
        logoIconTimeoutRef.current = setTimeout(() => {
          setShowLogoIcon(true);
        }, 300);
      }, 4000);
      
      // Hide finish prompt when text changes
      setShowFinishPrompt(false);
      
      // Clear existing finish prompt timeout
      if (finishPromptTimeoutRef.current) {
        clearTimeout(finishPromptTimeoutRef.current);
      }
      
      // Set timeout to show finish prompt after 3 seconds if there's text
      if (e.target.value.trim()) {
        finishPromptTimeoutRef.current = setTimeout(() => {
          setShowFinishPrompt(true);
        }, 1500);
      }
    }
  };

  // Handle activity in visible mode
  const handleVisibleActivity = () => {
    // Hide logo when there's activity
    setLogoVisible(false);
    setShowLogoText(false);
    setShowLogoIcon(false);
    
    // Clear existing logo timeouts
    if (logoTimeoutRef.current) {
      clearTimeout(logoTimeoutRef.current);
    }
    if (logoIconTimeoutRef.current) {
      clearTimeout(logoIconTimeoutRef.current);
    }
    
    // Set timeout to show logo again after 6 seconds of inactivity
    logoTimeoutRef.current = setTimeout(() => {
      setLogoVisible(true);
      setShowLogoText(true);
      // Show icon slightly after text
      logoIconTimeoutRef.current = setTimeout(() => {
        setShowLogoIcon(true);
      }, 300);
    }, 6000);
  };

  const handleCopy = async () => {
    const textToCopy = isVisible ? cleanedText : text;
    if (!textToCopy.trim()) return;
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Split text into chunks based on paragraphs and sentence boundaries
  const createTextChunks = (text) => {
    if (!text.trim()) return [];
    
    // Split by double newlines first (paragraphs)
    const paragraphs = text.split(/\n\s*\n/);
    const chunks = [];
    
    paragraphs.forEach((paragraph, index) => {
      const trimmed = paragraph.trim();
      if (trimmed) {
        chunks.push({
          id: `paragraph-${index}`,
          content: trimmed,
          originalContent: trimmed,
          processed: false
        });
      }
    });
    
    return chunks;
  };

  // Find differences between old and new chunk arrays
  const findChunkDifferences = (oldChunks, newText) => {
    const newChunks = createTextChunks(newText);
    const differences = {
      added: [],
      modified: [],
      unchanged: []
    };
    
    // Compare chunks
    newChunks.forEach((newChunk, index) => {
      const oldChunk = oldChunks[index];
      
      if (!oldChunk) {
        // New chunk added
        differences.added.push({ ...newChunk, index });
      } else if (oldChunk.originalContent !== newChunk.content) {
        // Chunk modified
        differences.modified.push({ ...newChunk, index, oldChunk });
      } else {
        // Chunk unchanged
        differences.unchanged.push({ ...oldChunk, index });
      }
    });
    
    return { differences, newChunks };
  };

  const cleanUpText = async (inputText, isIncremental = false, existingCleanedText = '') => {
    if (!inputText.trim()) return inputText;
    
    try {
      let systemPrompt = "You are a text editor assistant. Your job is to clean up text by fixing spelling and grammar errors, correcting typos, and adding appropriate paragraph breaks between different thoughts or topics. Do NOT change the meaning, tone, or style of the text. Do NOT add, remove, or rearrange content. Only fix errors and improve formatting with paragraph breaks. Return only the cleaned text without any additional commentary.";
      
      let userContent = inputText;
      
      if (isIncremental && existingCleanedText) {
        systemPrompt += " You are processing additional text that follows previously cleaned content. Ensure smooth transitions and appropriate paragraph breaks between the existing content and the new content.";
        userContent = `Previous cleaned text: "${existingCleanedText}"\n\nNew text to clean and append: "${inputText}"`;
      }

      const response = await openai.chat.completions.create({
        model: "gpt-4o-mini",
        messages: [
          {
            role: "system",
            content: systemPrompt
          },
          {
            role: "user",
            content: userContent
          }
        ],
        temperature: 0.1,
        max_tokens: 4000
      });

      return response.choices[0].message.content;
    } catch (error) {
      console.error('Error cleaning up text:', error);
      return isIncremental ? existingCleanedText + ' ' + inputText : inputText; // Return fallback if cleanup fails
    }
  };

  // Process only specific chunks that have changed
  const processChangedChunks = async (differences, existingCleanedChunks) => {
    const updatedChunks = [...existingCleanedChunks];
    
    // Process modified chunks
    for (const modifiedChunk of differences.modified) {
      try {
        const cleanedContent = await cleanUpText(modifiedChunk.content);
        updatedChunks[modifiedChunk.index] = {
          ...modifiedChunk,
          cleanedContent,
          processed: true
        };
      } catch (error) {
        console.error('Error processing chunk:', error);
        updatedChunks[modifiedChunk.index] = {
          ...modifiedChunk,
          cleanedContent: modifiedChunk.content,
          processed: false
        };
      }
    }
    
    // Process new chunks
    for (const newChunk of differences.added) {
      try {
        const cleanedContent = await cleanUpText(newChunk.content);
        updatedChunks[newChunk.index] = {
          ...newChunk,
          cleanedContent,
          processed: true
        };
      } catch (error) {
        console.error('Error processing new chunk:', error);
        updatedChunks[newChunk.index] = {
          ...newChunk,
          cleanedContent: newChunk.content,
          processed: false
        };
      }
    }
    
    return updatedChunks;
  };

  // Rebuild full text from processed chunks
  const rebuildTextFromChunks = (chunks) => {
    return chunks
      .filter(chunk => chunk && chunk.cleanedContent !== undefined)
      .map(chunk => chunk.cleanedContent || chunk.content)
      .join('\n\n');
  };

  return (
    <div className="app">
      {showLogo && (
        <GhostLogo isVisible={logoVisible} showText={showLogoText} showIcon={showLogoIcon} />
      )}
      <button 
        className={`dark-mode-toggle ${logoVisible || showUIElements ? 'visible' : ''}`}
        onClick={toggleDarkMode}
        title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
      >
        {isDarkMode ? <SunIcon /> : <MoonIcon />}
      </button>
      <div className="writing-container">
        {!isVisible ? (
          <>
            {isCleaningText && (
              <div className="cleaning-overlay">
                <div className="cleaning-spinner"></div>
                <p>Cleaning up...</p>
              </div>
            )}
            <div className={`prompt ${isCleaningText ? 'cleaning' : ''}`}>
              <QuillIcon isAnimated={isTyping} />
              <span 
                className={`prompt-text ${!text.trim() ? '' : 'finish-prompt'} ${showFinishPrompt && text.trim() ? 'fade-in' : ''} ${showFinishPrompt && text.trim() ? 'clickable' : ''}`}
                onClick={showFinishPrompt && text.trim() ? handleReveal : undefined}
              >
                {!text.trim() ? 'start writing' : showFinishPrompt ? getRevealMessage() : ''}
              </span>
            </div>
            <textarea
              ref={textareaRef}
              className={`invisible-textarea ${isTransitioning ? 'transitioning' : ''}`}
              value={isTransitioning ? cleanedText : text}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder=""
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              readOnly={isCleaningText || isTransitioning}
            />
          </>
        ) : (
          <div className="visible-editor">
            <div className="editor-header">
              <span 
                className="edit-prompt clickable"
                onClick={handleReveal}
              >
                {getKeepWritingMessage()}
              </span>
            </div>
            <textarea
              ref={visibleTextareaRef}
              className="visible-textarea"
              value={cleanedText}
              onChange={(e) => {
                setCleanedText(e.target.value);
                // Handle activity for logo visibility
                handleVisibleActivity();
              }}
              onKeyDown={(e) => {
                handleKeyDown(e);
                // Handle activity for logo visibility on any key press
                handleVisibleActivity();
              }}
              onFocus={handleVisibleActivity}
              onClick={handleVisibleActivity}
              placeholder="Your writing appears here..."
              spellCheck={true}
              autoComplete="off"
              autoCorrect="on"
              autoCapitalize="on"
            />
          </div>
        )}
      </div>
      
      {(text.trim() || cleanedText.trim()) && (
        <button 
          className={`copy-button ${copyFeedback ? 'copied' : ''} ${logoVisible || isVisible || showUIElements ? 'visible' : ''}`}
          onClick={handleCopy}
          title="Copy text to clipboard"
        >
          {copyFeedback ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2" fill="none"/>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
          )}
        </button>
      )}
    </div>
  );
}

export default App;